<template>
  <div v-if="apiKey" id="gc-wc-funds" :style="style" ref="gc-wc-funds">
    <h1 class="title">{{ title }}</h1>
    <p>{{ description }}</p>
    <div class="refine-funds" :class="{ 'full-width': cols === 1}">
      <form class="search" v-on:submit="handleSearch">
        <input type="text" placeholder="Search Funds" v-model="searchTerm">
        <button type="submit">
          <span class="sr-only">Search</span>
        </button>
      </form>
      <span>Filter by</span>
      <select v-model="selectedLocation" v-on:change="selectAndGetList('location')">
        <option value="">Location</option>
        <option v-for="(location, index) in locations" :key="index" :value="location.text">
          {{ location.text}} ({{ location.count }})
        </option>
      </select>
      <span>or</span>
      <select v-model="selectedCategory" v-on:change="selectAndGetList('category')">
        <option value="">Category</option>
        <optgroup v-for="category in categories" :key="category.id" :label="category.name">
          <option v-for="child in category.children" :key="child.id" :value="child.slug">
            {{ child.name}} ({{ child.count }})
          </option>
        </optgroup>
      </select>
    </div>
    <div v-if="loading" class="spinner-wrapper" :style="{ height: `${spinnerHeight}px`, margin: `${renderListAs === 'images' ? -12 : 14}px` }">
      <div class="spinner" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </div>
    <p v-else-if="error === CLIENT_ERROR" class="error">We encountered a network error. Please check your internet connection and try again.</p>
    <p v-else-if="error === NO_RESULTS" class="error">
      There doesn’t seem to be a match for the fund you are looking for.
      <a href="#" v-on:click="reset">Click here to see all available funds.</a>
    </p>
    <p v-else-if="error === SERVER_ERROR" class="error">We’re sorry, we can’t retrieve results right now. Please try again later.</p>
    <ul ref="funds" v-else class="funds" :class="`funds-as-${this.renderListAs}`">
      <li v-for="fund in funds" :key="fund.ID" :style="liStyle">
        <a v-bind:href="getUrlWithWcTrackingParams(fund.fund_url)" target="_blank">
          <img v-if="renderListAs === 'images'" :src="fund.gc_medium_image">
          <span>{{ fund.title }}</span>
        </a>
      </li>
    </ul>
    <div class="pagination">
      <b-pagination
        v-model="pagination.page"
        v-on:change="handlePagination"
        :total-rows="pagination.total"
        :per-page="perPage"
        :limit="getPaginationLimitForScreenSize"
        aria-controls="list-results"
        first-text="|<"
        last-text=">|"
        next-text=">"
        prev-text="<"
        first-class="gc-pagination-first"
        prev-class="gc-pagination-prev"
        page-class="gc-pagination-page"
        ellipsis-class="gc-pagination-ellipsis"
        next-class="gc-pagination-next"
        last-class="gc-pagination-last"
      />
    </div>
    <div>
      <a class="powered-by" v-bind:href="getUrlWithWcTrackingParams('https://givingcompass.org')" target="_blank">
        Powered by
        <span class="sr-only">Giving Compass</span>
      </a>
    </div>
  </div>
</template>

<script>
import fundService from '../services/fund';
import { BPagination } from 'bootstrap-vue';

const DEFAULT_MAX_WIDTH = '985px';
const DEFAULT_MIN_WIDTH = '260px';
const PIXELS_BETWEEN_IMAGES = 24;

export default {
  components: {
    BPagination,
  },
  props: {
    apiKey: {
      type: String,
      default: null,
    },
    description: {
      type: String,
      default: 'Find a range of vetted funds addressing immediate and long-term needs.',
    },
    excludeCategories: {
      type: String,
      default: null,
    },
    excludeTags: {
      type: String,
      default: null,
    },
    excludeTerms: {
      type: String,
      default: null,
    },
    includeCategories: {
      type: String,
      default: null,
    },
    includeTags: {
      type: String,
      default: null,
    },
    includeTerms: {
      type: String,
      default: null,
    },
    maxWidth: {
      type: String,
      default: null,
      validator: (value) => {
        return /^\d+px$/.test(value);
      },
    },
    perPage: {
      type: [Number, String],
      default: 6,
    },
    renderListAs: {
      type: String,
      default: 'images',
      validator: (value) => {
        return ['images', 'links'].includes(value);
      },
    },
    title: {
      type: String,
      default: 'Issue Funds and Intermediaries',
    },
    width: {
      type: String,
      default: null,
      validator: (value) => {
        return /^\d+px$/.test(value);
      },
    },
    devApiHost: {
      type: String,
      default: undefined,
    },
    devGtmId: {
      type: String,
      default: 'GTM-P25774K'
    },
    devTheme: {
      type: String,
      default: null,
    },
  },
  data() {
    const maxWidth = this.maxWidth || DEFAULT_MAX_WIDTH;
    let cols = null;
    let titleSize = null;

    if (this.width || this.maxWidth) {
      const targetWidth = this.width ? parseInt(this.width, 10) : parseInt(maxWidth, 10);
      switch (true) {
        case targetWidth <= 380:
          cols = 1;
          titleSize = '24px';
          break;
        case targetWidth > 381 && targetWidth <= 680:
          cols = 2;
          titleSize = '32px';
          break;
        case targetWidth < 1320:
          cols = 3;
          break;
        default:
          cols = 4;
          break;
      }
    }

    // TODO Move this to a more manageable place
    const themes = {
      dark: {
        '--bg-color': '#000',
        '--text-color': '#FFF',
      },
    };

    return {
      CLIENT_ERROR: 'CLIENT_ERROR',
      NO_RESULTS: 'NO_RESULTS',
      SERVER_ERROR: 'SERVER_ERROR',
      categories: [],
      cols,
      error: '',
      funds: [],
      loading: false,
      pagination: {
        total: 0,
        page: 1,
      },
      locations: [],
      searchTerm: '',
      selectedCategory: '',
      selectedLocation: '',
      service: {},
      spinnerHeight: null,
      style: {
        maxWidth,
        minWidth: DEFAULT_MIN_WIDTH,
        width: this.width,
        '--title-size': titleSize,
        ...themes[this.devTheme] || {},
      },
      utmMedium: 'adv_widget',
      utmSource: '',
    };
  },
  computed: {
    liStyle() {
      if (this.renderListAs === 'links') {
        return {};
      }
      return {
        margin: `${PIXELS_BETWEEN_IMAGES / 2}px`,
        width: this.cols ? `calc(${100 / this.cols}% - ${PIXELS_BETWEEN_IMAGES}px)` : '300px',
      };
    },
     getPaginationLimitForScreenSize() {
       if (window.innerWidth < 500) {
         return 3;
       } else if (this.maxWidth === null) {
         return 5;
       } else {
        return (Number(this.maxWidth.replace('px','')) < 500 )? 3 : 5;
       }
    },
  },
  mounted() {
    this.loadGTM();
    this.service = fundService.create(this.apiKey, this.devApiHost);
    this.getList();
    this.getOptions();
  },
  methods: {
    async loadGTM() {
      if (!this.devGtmId || (this.devGtmId.toLowerCase() === 'false')) {
        return;
      }
      let gtmScript = document.createElement('script');
      gtmScript.async = true;
      gtmScript.setAttribute('src', 'https://www.googletagmanager.com/gtag/js?id=' + this.devGtmId);
      document.head.appendChild(gtmScript);
      window.dataLayer = window.dataLayer || [];
      function gtag(){window.dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', this.devGtmId, {
        'linker': {
          'domains': ['givingcompass.org']
        }
      });
    },
    async getList() {
      this.error = '';
      this.setLoading(true);
      try {
        const response = await this.service.getList({
          excludeCategories: this.excludeCategories,
          excludeTags: this.excludeTags,
          includeCategories: this.includeCategories,
          includeTags: this.includeTags,
          page: this.pagination.page,
          per_page: this.perPage,
          tags: this.tags,
          categories: this.selectedCategory,
          locations: this.selectedLocation,
        });
        this.funds = response.data;
        this.pagination.total = response.meta.pagination.total;
        if (this.utmSource === '' && response.data[0].fund_url) {
          this.utmSource = this.service.getUTMSourceParam(response.data[0].fund_url);
        }
      } catch (error) {
        this.setError(error);
      } finally {
        this.setLoading(false);
      }
    },
    async handlePagination(newPage) {
      this.pagination.page = newPage;
      if (this.searchTerm !== '') {
        //handle search pagination
        await this.searchList();
      } else {
        //handle non-search pagination
        await this.getList();
      }
      this.$refs['gc-wc-funds'].scrollIntoView();
    },
    handleSearch (event) {
      if (event) {
        event.preventDefault();
      }
      this.pagination.page = 1;
      this.searchList();
    },
    async getOptions() {
      try {
        const response = await this.service.getOptions({ tags: this.includeTags || '' });
        this.locations = response.locations;
        this.categories = response.categories;
      } catch (error) {
        this.setError(error);
      }
    },
    getUrlWithWcTrackingParams(url = '') {
      if (!url) {
        return null;
      }

      const qsRegex = /(.*)\?(.*)/.exec(url);
      const params = new URLSearchParams(qsRegex ? qsRegex[2] : '');
      params.set('utm_medium', this.utmMedium);
      params.set('utm_source', this.utmSource);
      if (this.includeTags === 'coronavirus') {
        params.set('utm_campaign', 'covid_funds');
      }
      return `${qsRegex ? qsRegex[1] : url}?${params.toString()}`;
    },
    reset (event) {
      event.preventDefault();
      this.error = '';
      this.searchTerm = '';
      this.selectedCategory = '';
      this.selectedLocation = '';
      this.getList();
    },
    async searchList() {
      this.error = '';
      this.selectedCategory = '';
      this.selectedLocation = '';
      this.setLoading(true);
      try {
        const response = await this.service.getSearchResults({
          excludeCategories: this.excludeCategories,
          excludeTags: this.excludeTags,
          excludeTerms: this.excludeTerms,
          includeCategories: this.includeCategories,
          includeTags: this.includeTags,
          includeTerms: this.includeTerms,
          query: this.searchTerm,
          page: this.pagination.page,
          per_page: this.perPage,
          tags: this.tags,
        });
        this.funds = response.data;
        this.pagination.total = response.meta.pagination.total;
        if (this.funds.length === 0) {
          this.error = this.NO_RESULTS;
        }
      } catch (error) {
        this.setError(error);
      } finally {
        this.setLoading(false);
      }
    },
    selectAndGetList(filter) {
      this.searchTerm = '';
      if (filter === 'category') {
        this.selectedLocation = '';
      }
      if (filter === 'location') {
        this.selectedCategory = '';
      }
      this.pagination.page = 1;
      this.getList();
    },
    setError(error) {
      this.error = error.response ? this.SERVER_ERROR : this.CLIENT_ERROR;
    },
    setLoading(enabled) {
      if (enabled && this.$refs.funds) {
        this.spinnerHeight = this.$refs.funds.getBoundingClientRect().height || null;
      }
      this.loading = enabled;
    },
  },
}
</script>

<style src="./pagination.scss" lang="scss"></style>
<style src="./gc-wc-funds.scss" lang="scss" scoped></style>
